import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Link,
  MenuItem,
  Select,
  Switch,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { i18next } from '@ingenico/react-i18n';
import { DEFAULT_LNGS } from '@ingenico/util-i18n-config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { STANDALONE_LANGUAGE_LABELS } from '../constants/standaloneLanguage.const';
import { USER_TYPES, type StandaloneUserType } from '../constants/userTypeSwitch.const';
import { StandaloneEnvFlagsService } from '../services/standaloneEnvFlags.service';
import { StandaloneLanguageService } from '../services/standaloneLanguage.service';
import { StandalonePermissionOverridesService } from '../services/standalonePermissionOverrides.service';
import { UserTypeSwitchService } from '../services/userTypeSwitch.service';

const USER_TYPE_COLORS: Record<StandaloneUserType, string> = {
  [USER_TYPES.customer]: '#4caf50',
  [USER_TYPES.tenant]: '#757575',
  [USER_TYPES.developer]: '#abc9ff',
};

const USER_TYPE_OPTIONS = Object.values(USER_TYPES);

const CAPABILITIES_REFERENCE_URL =
  'https://gitlab.com/ingenico-cloud-platform/icp/applications/solution/portals/device-management/-/blob/main/device-management-mfe/src/standalone-app/hexa/adapters/deviceManagementSpi.adapter.ts?ref_type=heads';

export function FloatingUserTypeSwitch() {
  const [currentUserType, setCurrentUserType] = useState<StandaloneUserType>(() =>
    UserTypeSwitchService.getCurrentUserType(),
  );
  const [isMocksEnabled, setIsMocksEnabled] = useState(() => UserTypeSwitchService.isApiSdkMocksEnabled());
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    () => StandaloneLanguageService.getSessionLanguage() ?? i18next.language ?? DEFAULT_LNGS,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgNameInput, setOrgNameInput] = useState('');
  const [pendingUserType, setPendingUserType] = useState<StandaloneUserType | null>(null);
  const [isEnvModalOpen, setIsEnvModalOpen] = useState(false);
  const [envFlagDraft, setEnvFlagDraft] = useState<Record<string, 'true' | 'false'>>({});
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [permissionOverrideDraft, setPermissionOverrideDraft] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setCurrentUserType(UserTypeSwitchService.getCurrentUserType());
  }, []);

  const backgroundColor = useMemo(() => USER_TYPE_COLORS[currentUserType], [currentUserType]);
  const editableEnableFlags = useMemo(() => StandaloneEnvFlagsService.getEditableEnableFlagKeys(), []);
  const availableLanguages = useMemo(() => StandaloneLanguageService.getAvailableLanguages(), []);

  const handleLanguageChange = useCallback((event: SelectChangeEvent<string>) => {
    const nextLanguage = event.target.value;
    setCurrentLanguage(nextLanguage);
    StandaloneLanguageService.setSessionLanguage(nextLanguage);
    void i18next.changeLanguage(nextLanguage);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setPendingUserType(null);
  }, []);

  const handleModalOk = useCallback(() => {
    if (pendingUserType !== null) {
      UserTypeSwitchService.setOrganizationNameForUserType(pendingUserType, orgNameInput);

      setCurrentUserType(pendingUserType);
      UserTypeSwitchService.setUserTypeDirectly(pendingUserType);
      UserTypeSwitchService.dispatchToggleEventForUserType(pendingUserType);
    }

    handleModalClose();
  }, [handleModalClose, orgNameInput, pendingUserType]);

  const handleSelectChange = useCallback((event: SelectChangeEvent<StandaloneUserType>) => {
    const newUserType = event.target.value as StandaloneUserType;
    const currentOrgName = UserTypeSwitchService.getOrganizationNameForMode(newUserType);

    setOrgNameInput(currentOrgName);
    setPendingUserType(newUserType);
    setIsModalOpen(true);
  }, []);

  const handleMocksToggle = useCallback(() => {
    const nextEnabled = UserTypeSwitchService.toggleApiSdkMocksEnabled();
    setIsMocksEnabled(nextEnabled);
    window.location.reload();
  }, []);

  const refreshEnvDraftForCurrentUserType = useCallback(() => {
    setEnvFlagDraft(StandaloneEnvFlagsService.getResolvedFlagValues(currentUserType, process.env));
  }, [currentUserType]);

  const handleOpenEnvModal = useCallback(() => {
    refreshEnvDraftForCurrentUserType();
    setIsEnvModalOpen(true);
  }, [refreshEnvDraftForCurrentUserType]);

  const handleCloseEnvModal = useCallback(() => {
    setIsEnvModalOpen(false);
  }, []);

  const handleApplyEnvFlags = useCallback(() => {
    StandaloneEnvFlagsService.setUserTypeOverrides(currentUserType, envFlagDraft);
    window.location.reload();
  }, [currentUserType, envFlagDraft]);

  const handleResetEnvFlags = useCallback(() => {
    StandaloneEnvFlagsService.resetUserTypeOverrides(currentUserType);
    refreshEnvDraftForCurrentUserType();
  }, [currentUserType, refreshEnvDraftForCurrentUserType]);

  const handleFlagToggle = useCallback((key: string, enabled: boolean) => {
    setEnvFlagDraft((previousDraft) => ({
      ...previousDraft,
      [key]: enabled ? 'true' : 'false',
    }));
  }, []);

  const availablePermissions = useMemo(() => StandalonePermissionOverridesService.getAvailablePermissions(), []);

  const refreshPermissionDraftForCurrentUserType = useCallback(() => {
    setPermissionOverrideDraft(
      StandalonePermissionOverridesService.getEffectivePermissionAvailabilityState(currentUserType),
    );
  }, [currentUserType]);

  const handleOpenPermissionModal = useCallback(() => {
    refreshPermissionDraftForCurrentUserType();
    setIsPermissionModalOpen(true);
  }, [refreshPermissionDraftForCurrentUserType]);

  const handleClosePermissionModal = useCallback(() => {
    setIsPermissionModalOpen(false);
  }, []);

  const handleApplyPermissionOverrides = useCallback(() => {
    const overridesToSet = StandalonePermissionOverridesService.buildOverridesFromAvailabilityState(
      currentUserType,
      permissionOverrideDraft,
    );
    StandalonePermissionOverridesService.setUserTypeOverrides(currentUserType, overridesToSet);
    window.location.reload();
  }, [currentUserType, permissionOverrideDraft]);

  const handleResetPermissionOverrides = useCallback(() => {
    StandalonePermissionOverridesService.resetUserTypeOverrides(currentUserType);
    refreshPermissionDraftForCurrentUserType();
  }, [currentUserType, refreshPermissionDraftForCurrentUserType]);

  const handlePermissionToggle = useCallback((permission: string, value: boolean) => {
    setPermissionOverrideDraft((previousDraft) => ({
      ...previousDraft,
      [permission]: value,
    }));
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        zIndex: 9999,
        minWidth: 128,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.35 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={currentUserType}
            onChange={handleSelectChange}
            MenuProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
            sx={{
              backgroundColor,
              color: '#fff',
              borderRadius: 2,
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '.MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            {USER_TYPE_OPTIONS.map((userType) => (
              <MenuItem key={userType} value={userType}>
                {userType.charAt(0).toUpperCase() + userType.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <Select
            value={currentLanguage}
            onChange={handleLanguageChange}
            MenuProps={{
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              sx: { zIndex: 10000 },
            }}
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              fontSize: '0.72rem',
              minHeight: 28,
              '.MuiSelect-select': {
                py: 0.35,
              },
            }}
          >
            {availableLanguages.map((language) => (
              <MenuItem key={language} value={language}>
                {STANDALONE_LANGUAGE_LABELS[language] ?? language}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="contained"
          onClick={handleMocksToggle}
          sx={{
            width: '100%',
            backgroundColor: isMocksEnabled ? '#4caf50' : '#f44336',
            color: '#fff',
            textTransform: 'none',
            py: 0.35,
            px: 0.8,
            minHeight: 28,
            fontSize: '0.72rem',
            '&:hover': {
              backgroundColor: isMocksEnabled ? '#43a047' : '#e53935',
            },
          }}
        >
          {isMocksEnabled ? 'Mocks ON' : 'Mocks OFF'}
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleOpenEnvModal}
          sx={{
            width: '100%',
            textTransform: 'none',
            py: 0.35,
            px: 0.8,
            minHeight: 28,
            fontSize: '0.72rem',
          }}
        >
          Env Flags
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleOpenPermissionModal}
          sx={{
            width: '100%',
            textTransform: 'none',
            py: 0.35,
            px: 0.8,
            minHeight: 28,
            fontSize: '0.72rem',
          }}
        >
          Permissions
        </Button>
      </Box>

      <Dialog open={isModalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingUserType
            ? `${pendingUserType.charAt(0).toUpperCase() + pendingUserType.slice(1)} Organization`
            : 'Organization'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Organization Name"
              value={orgNameInput}
              onChange={(event) => setOrgNameInput(event.target.value)}
              variant="outlined"
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button variant="contained" onClick={handleModalOk}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEnvModalOpen} onClose={handleCloseEnvModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Standalone env flags ({currentUserType})</Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            For a reference of how the capabilities (Env Flags + permissions) are used check the code adapter here (
            <Link href={CAPABILITIES_REFERENCE_URL} target="_blank" rel="noopener noreferrer">
              link
            </Link>
            )
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Edit only NX_PUBLIC_ENABLE_* flags for the current shell. Applying reloads the page.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gap: 1 }}>
            {editableEnableFlags.map((flagKey) => {
              const isEnabled = envFlagDraft[flagKey] === 'true';
              return (
                <FormControlLabel
                  key={flagKey}
                  control={<Switch checked={isEnabled} onChange={(_, checked) => handleFlagToggle(flagKey, checked)} />}
                  label={flagKey}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetEnvFlags}>Reset Defaults</Button>
          <Button onClick={handleCloseEnvModal}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyEnvFlags}>
            Apply & Reload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isPermissionModalOpen} onClose={handleClosePermissionModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Permission Overrides ({currentUserType})</Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            For a reference of how the capabilities (Env Flags + permissions) are used check the code adapter here (
            <Link href={CAPABILITIES_REFERENCE_URL} target="_blank" rel="noopener noreferrer">
              link
            </Link>
            )
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Override dm.* permissions as available. Turn off to remove permission. Applied overrides trigger a page
            reload.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gap: 1, maxHeight: 400, overflow: 'auto' }}>
            {availablePermissions.map((permission) => {
              const currentValue = permissionOverrideDraft[permission];
              return (
                <Box key={permission} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>{permission}</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentValue === true}
                        onChange={(_, checked) => handlePermissionToggle(permission, checked)}
                      />
                    }
                    label="Available"
                    sx={{ minWidth: 110 }}
                  />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPermissionOverrides}>Reset Defaults</Button>
          <Button onClick={handleClosePermissionModal}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyPermissionOverrides}>
            Apply & Reload
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
